import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { api, ApiError } from '../api/client';
import type { ContractsConfig, IssueRequest, StHoldings, TxRequestResult, Wallet } from '../api/types';
import { PageHeader, shortAddr, fmtUnits, TxHashLink } from '../components/common';
import { TxProgressDialog, TxResult } from '../components/TxProgressDialog';

/** 전송 대상 — 스테이블코인(KRWS) 또는 보유 토큰증권 */
interface TransferTarget {
  kind: 'SC' | 'ST';
  contract: string;
  symbol: string;
  /** 이동 가능 수량(wei) — 부분 동결분을 제외한 값 */
  transferable: string;
}

export function Assets() {
  const qc = useQueryClient();
  const wallets = useQuery({ queryKey: ['my-wallets'], queryFn: () => api.get<Wallet[]>('/users/me/wallets') });
  const cfg = useQuery({ queryKey: ['contracts'], queryFn: () => api.get<ContractsConfig>('/config/contracts') });

  const wallet = wallets.data?.find((w) => w.keyStatus === 'ACTIVE')?.walletAddress ?? wallets.data?.[0]?.walletAddress;
  const sc = cfg.data?.stablecoin ?? null;

  // 잔액만으로는 쓸 수 있는 상태인지 알 수 없다 — 동결되면 잔액이 있어도 전송이 막힌다
  const balance = useQuery({
    queryKey: ['sc-balance', sc, wallet],
    queryFn: () => api.get<{ wallet: string; balance: string; frozen: boolean; transferable: string }>(
      `/stablecoin/${sc}/freeze-status/${wallet}`,
    ),
    enabled: !!sc && !!wallet,
  });

  // A-5: 토큰증권 보유 현황(잔액·동결·이동 가능 수량)
  const holdings = useQuery({
    queryKey: ['st-holdings'],
    queryFn: () => api.get<StHoldings>('/securities/holdings'),
    enabled: !!wallet,
  });

  const [target, setTarget] = useState<TransferTarget | null>(null);
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [txOpen, setTxOpen] = useState(false);
  const [result, setResult] = useState<TxResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const openTransfer = (t: TransferTarget) => { setTarget(t); setTo(''); setAmount(''); };

  /**
   * 전송 — 대상·수신자·수량을 **변수로 넘긴다**.
   * `onMutate` 는 `mutationFn` 보다 먼저 실행되므로, 거기서 다이얼로그를 닫으며 `target` 을 비우면
   * 뒤이어 실행되는 `mutationFn` 이 빈 값을 참조해 요청이 나가지도 못한 채 실패한다.
   */
  const transfer = useMutation({
    mutationFn: (v: { target: TransferTarget; to: string; amount: string }) => {
      const base = v.target.kind === 'SC' ? '/stablecoin' : '/securities';
      return api.post<TxRequestResult>(`${base}/${v.target.contract}/transfer`, { to: v.to, amount: v.amount });
    },
    onMutate: () => { setTarget(null); setTxOpen(true); setResult(null); setError(null); },
    onSuccess: (r) => {
      setResult({ status: r.status, txHash: r.txHash, revertReason: r.revertReason });
      void qc.invalidateQueries({ queryKey: ['sc-balance'] });
      void qc.invalidateQueries({ queryKey: ['st-holdings'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '전송 실패'),
  });

  const st = holdings.data?.holdings ?? [];

  return (
    <Box>
      <PageHeader
        crumb="서비스 사용자 · 자산"
        title="내 자산"
        desc="보유 지갑의 스테이블코인(KRWS)과 토큰증권 잔액입니다. 가스비는 서비스가 대납합니다."
      />
      {(wallets.isLoading || cfg.isLoading) && <LinearProgress />}

      <Paper sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>자산</TableCell>
              <TableCell align="right">잔액</TableCell>
              <TableCell align="right">이동 가능</TableCell>
              <TableCell>지갑</TableCell>
              <TableCell align="right">액션</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {wallet && sc && (
              <TableRow hover>
                <TableCell>
                  <b>KRWS</b>{' '}
                  <Typography component="span" variant="caption" color="text.secondary">스테이블코인</Typography>
                  {balance.data?.frozen && (
                    <Chip size="small" color="error" label="계정 동결" sx={{ ml: 1, fontWeight: 700 }} />
                  )}
                </TableCell>
                <TableCell align="right">{balance.isLoading ? '…' : `${fmtUnits(balance.data?.balance)} KRWS`}</TableCell>
                <TableCell align="right">
                  {balance.isLoading ? '…' : `${fmtUnits(balance.data?.transferable)} KRWS`}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{shortAddr(wallet)}</TableCell>
                <TableCell align="right">
                  <Tooltip title={balance.data?.frozen ? '지갑이 동결되어 송·수신이 모두 차단됩니다(발행자 문의)' : ''}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!!balance.data?.frozen}
                        onClick={() => openTransfer({ kind: 'SC', contract: sc, symbol: 'KRWS', transferable: balance.data?.transferable ?? '0' })}
                      >
                        전송
                      </Button>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            )}

            {/* A-5: 토큰증권 보유분 */}
            {st.map((h) => {
              const locked = h.frozen || h.transferable === '0';
              return (
                <TableRow hover key={h.contract}>
                  <TableCell>
                    <b>{h.symbol}</b>{' '}
                    <Typography component="span" variant="caption" color="text.secondary">
                      토큰증권{h.title ? ` · ${h.title}` : ''}
                    </Typography>
                    {h.frozen && <Chip size="small" color="error" label="계정 동결" sx={{ ml: 1, fontWeight: 700 }} />}
                    {!h.frozen && h.frozenTokens !== '0' && (
                      <Tooltip title={`부분 동결 ${fmtUnits(h.frozenTokens)} ${h.symbol}`}>
                        <Chip size="small" color="warning" label="부분 동결" sx={{ ml: 1, fontWeight: 700 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">{fmtUnits(h.balance)} {h.symbol}</TableCell>
                  <TableCell align="right">{fmtUnits(h.transferable)} {h.symbol}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{shortAddr(wallet)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={locked ? '동결되어 이동할 수 없습니다(발행자 문의)' : ''}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={locked}
                          onClick={() => openTransfer({ kind: 'ST', contract: h.contract, symbol: h.symbol, transferable: h.transferable })}
                        >
                          전송
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}

            {wallet && sc && st.length === 0 && !holdings.isLoading && (
              <TableRow>
                <TableCell colSpan={5} sx={{ color: 'text.secondary', py: 2 }}>
                  보유 중인 토큰증권이 없습니다. 청약에 참여하고 발행이 완료되면 여기에 표시됩니다.
                </TableCell>
              </TableRow>
            )}
            {!wallet && !wallets.isLoading && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>지갑이 아직 없습니다(승인·키 발급 대기).</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* 청약 예치금 — 에스크로에 묶여 잔액에서 빠진 KRWS (1차 확인 19번) */}
      {wallet && <EscrowDepositsPanel />}

      {/* A-6: 스테이블코인 발행 요청 */}
      {wallet && sc && <IssueRequestPanel stablecoin={sc} />}

      <Dialog open={!!target} onClose={() => setTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{target?.symbol} 전송</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="받는 주소" value={to} onChange={(e) => setTo(e.target.value)} placeholder="0x…" />
            <TextField
              label={`수량 (${target?.symbol ?? ''})`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="number"
              helperText={target ? `이동 가능 ${fmtUnits(target.transferable)} ${target.symbol}` : undefined}
            />
            <Typography variant="caption" color="text.secondary">
              수신자는 온체인 isVerified(적격) 상태여야 하며, 미검증 시 전송이 거부됩니다.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTarget(null)}>취소</Button>
          <Button
            variant="contained"
            onClick={() => target && transfer.mutate({ target, to, amount })}
            disabled={!to || !amount}
          >
            전송
          </Button>
        </DialogActions>
      </Dialog>

      <TxProgressDialog
        open={txOpen}
        title="자산 전송"
        pending={transfer.isPending}
        result={result}
        error={error}
        onClose={() => setTxOpen(false)}
      />
    </Box>
  );
}

interface MyDeposits {
  wallet: string | null;
  escrow: string | null;
  totalDeposited: string;
  rows: {
    subscriptionId: string; tokenName: string; symbol: string; subStatus: string; unitPrice: string;
    amount: string; appStatus: string[]; escrowTxHash: string | null; appliedAt: string;
    /** 에스크로 예치 잔액(wei). null = 체인 조회 실패 */
    deposited: string | null;
  }[];
}

const SUB_STATUS_LABEL: Record<string, string> = { OPEN: '청약 중', CLOSED: '마감', ISSUED: '발행 완료', SETTLED: '정산 완료' };

/**
 * 청약 예치금 (SFR-003).
 * 청약 신청 시 KRWS 는 에스크로 컨트랙트로 옮겨져 위 잔액에서 빠진다. 그 금액이 어디에 얼마 있는지
 * 공고별로 보여준다 — 잔액은 온체인 deposits() 기준이며, 정산·환불 뒤에는 0 이 된다.
 */
function EscrowDepositsPanel() {
  const q = useQuery({ queryKey: ['my-deposits'], queryFn: () => api.get<MyDeposits>('/subscriptions/deposits/mine') });
  const rows = q.data?.rows ?? [];
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1}>
        <Typography variant="h2">청약 예치금 (에스크로)</Typography>
        <Typography fontWeight={700}>합계 {fmtUnits(q.data?.totalDeposited)} KRWS</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        청약 신청 금액은 에스크로 컨트랙트{q.data?.escrow ? ` (${shortAddr(q.data.escrow)})` : ''}에 예치되어 위 KRWS 잔액에 포함되지 않습니다.
        발행 후 정산되면 발행자에게, 미배정·취소 시 환불로 지갑에 돌아옵니다.
      </Typography>
      {q.isLoading && <LinearProgress />}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>공고</TableCell><TableCell>공고 상태</TableCell>
            <TableCell align="right">신청 수량</TableCell><TableCell align="right">예치 잔액</TableCell>
            <TableCell>예치 트랜잭션</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.subscriptionId} hover>
              <TableCell><b>{r.tokenName}</b> <Typography component="span" variant="caption" color="text.secondary">{r.symbol} · #{r.subscriptionId}</Typography></TableCell>
              <TableCell><Chip size="small" label={SUB_STATUS_LABEL[r.subStatus] ?? r.subStatus} /></TableCell>
              <TableCell align="right">{r.amount} 주</TableCell>
              <TableCell align="right">
                {r.deposited === null ? <Typography variant="caption" color="text.secondary">조회 불가</Typography> : `${fmtUnits(r.deposited)} KRWS`}
              </TableCell>
              <TableCell><TxHashLink hash={r.escrowTxHash} /></TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && !q.isLoading && (
            <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 2 }}>청약 신청 내역이 없습니다.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}

/**
 * 스테이블코인 발행 요청 (SFR-005, A-6).
 * 사용자가 요청하고 발행자가 Registry 등록을 확인한 뒤 발행한다 — 여기서는 신청과 진행 상황만 다룬다.
 */
function IssueRequestPanel({ stablecoin }: { stablecoin: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['my-issue-requests', stablecoin],
    queryFn: () => api.get<IssueRequest[]>(`/stablecoin/${stablecoin}/issue-requests/mine`),
  });

  const create = useMutation({
    mutationFn: () => api.post<IssueRequest>(`/stablecoin/${stablecoin}/issue-requests`, { amount, memo }),
    onSuccess: () => {
      setOpen(false); setAmount(''); setMemo(''); setError(null);
      void qc.invalidateQueries({ queryKey: ['my-issue-requests'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '요청 실패'),
  });

  const rows = list.data ?? [];
  const pending = rows.some((r) => r.status === 'PENDING');

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h2">스테이블코인 발행 요청</Typography>
          <Typography variant="body2" color="text.secondary">
            요청하면 발행자가 온체인 등록(isVerified)을 확인한 뒤 내 지갑으로 KRWS를 발행합니다.
          </Typography>
        </Box>
        <Tooltip title={pending ? '심사 대기 중인 요청이 있습니다' : ''}>
          <span>
            <Button variant="contained" disabled={pending} onClick={() => { setError(null); setOpen(true); }}>
              발행 요청
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {rows.length === 0 && !list.isLoading && (
        <Typography variant="body2" color="text.secondary">요청 이력이 없습니다.</Typography>
      )}
      {rows.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>요청일</TableCell><TableCell align="right">수량</TableCell>
              <TableCell>상태</TableCell><TableCell>비고</TableCell><TableCell>트랜잭션</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{new Date(r.createdAt).toLocaleString('ko-KR')}</TableCell>
                <TableCell align="right">{r.amount} KRWS</TableCell>
                <TableCell><IssueStatusChip status={r.status} /></TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{r.rejectReason ?? r.memo ?? '—'}</TableCell>
                <TableCell><TxHashLink hash={r.txHash} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>KRWS 발행 요청</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="요청 수량 (KRWS)" value={amount} onChange={(e) => setAmount(e.target.value)} type="number" />
            <TextField label="사유 (선택)" value={memo} onChange={(e) => setMemo(e.target.value)} multiline rows={2} />
            <Typography variant="caption" color="text.secondary">
              수령 지갑은 신청 시점의 활성 지갑으로 고정됩니다.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>취소</Button>
          <Button variant="contained" onClick={() => create.mutate()} disabled={!amount || create.isPending}>
            요청
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export function IssueStatusChip({ status }: { status: IssueRequest['status'] }) {
  if (status === 'PENDING') return <Chip size="small" color="warning" label="심사 대기" sx={{ fontWeight: 700 }} />;
  if (status === 'APPROVED') return <Chip size="small" color="success" label="발행 완료" sx={{ fontWeight: 700 }} />;
  return <Chip size="small" color="error" label="반려" sx={{ fontWeight: 700 }} />;
}
